<template>
  <ContainerBasic container-type="FULL_WIDTH">
    <div class="bg-[#99b6b8]">
      <ContainerBasic
        container-type="PAGE"
        classes="pt-md md:pt-xl pb-sm md:pb-md"
      >
        <div class="flex flex-col justify-between md:flex-row">
          <div
            class="px-md md:px-0 max-w-[690px] order-2 md:order-1 pt-md md:pt-0"
          >
            <h1 class="text-left text-white">
              {{ t('404.pageNotFound') }}
            </h1>
            <div class="text-left text-white py-sm">
              <p>{{ t('404.pageNotFoundDetails') }}</p>
            </div>
            <BasicButton
              classes="mt-auto text-white"
              :type="EButtonType.ROUTERLINK"
              :label="t('404.toHome')"
              target="/"
            />
          </div>
          <div class="order-1 px-md md:px-0 md:order-2">
            <img
              src="~/assets/images/404_teeth.png"
              class="object-contain"
              style="max-height: 175px"
            />
          </div>
        </div>
        <div class="flex flex-auto w-full pt-md">
          <img :src="imageSrc" class="flex flex-auto object-contain" />
        </div>
      </ContainerBasic>
    </div>

    <div class="w-full bg-vdv-base h-[36px]" />

    <div class="bg-[#346e73a6]">
      <ContainerBasic container-type="PAGE" classes="px-sm py-md  md:p-md">
        <div class="flex flex-col md:flex-row gap-sm md:gap-x-md md:gap-y-0">
          <div
            class="bg-[#99b6b8] w-[314px] sm:w-[450px] max-h-[162px] py-md mx-auto md:mx-0"
          >
            <HeadlineDynamic
              :headline="t('404.toSpecialDepartments')"
              level="h2"
              class="text-white fill-white stroke-white w-[270px]"
              :has-decoration="true"
            />
          </div>

          <div
            class="flex flex-wrap flex-row gap-sm md:gap-x-md md:gap-y-0 w-[314px] sm:w-[450px] md:w-full mx-auto md:mx-0"
          >
            <div
              v-for="(area, index) in areas"
              :key="index"
              class="basis-1/2 md:basis-1/5 shrink-0 w-full cursor-pointer max-w-[149px] h-[149px] sm:max-w-[140px] sm:h-[140px] md:max-w-[162px] md:h-[162px] p-sm grid content-between text-base md:text-lg text-white"
              :style="`background-color: ${area.color};`"
              @click="onNavigateToArea(area)"
            >
              <p>{{ area.title }}</p>
              <FaIcon
                :icon="{ ...area.icon, iconSet: 'fas-icons' }"
                classes="!w-[46px] !h-[46px] md:!w-[50px] md:!h-[50px] color-[#FFFFFF33]"
                :style="`color: #FFFFFF33`"
              />
            </div>
          </div>
        </div>
      </ContainerBasic>
    </div>
  </ContainerBasic>
</template>

<script setup lang="ts">
import { EButtonType } from '~/@types/basic-button';
import { useAreaStore } from '@/stores/useArea';
import BasicButton from '~~/src/components/components/core/basic-button/vdv/basic-button.vue';
import ContainerBasic from '@/components/components/container-basic.vue';
import HeadlineDynamic from '@/components/components/core/headline-dynamic/vdv/headline-dynamic.vue';
import { mq_breakpointIsMobile } from '@/injectionSymbols';
import { useImages } from '@/composables/useImages';
import { useNav } from '~~/src/stores/useNav';
import type { AreaData } from '~~/src/server/transformers/EntityTransformers/areaTransformer';
import FaIcon from '@/components/fa-icon.vue';

const areaStore = useAreaStore();
areaStore.setDefaultArea();

const navStore = useNav();
navStore.mainNav_setActiveItemByUri('/');

useHead({
  title: 'van der Ven - 404',
  meta: [
    {
      name: 'description',
      content: '404 - Page not found',
    },
  ],
  style: [
    {
      id: 'areaStyles',
      innerHTML: `:root {${areaStore.areaStyles};${areaStore.activeAreaStyles}}`,
    },
  ],
});
const { t } = useTrans();
const isMobile = inject(mq_breakpointIsMobile);
const areas = await areaStore.get404Areas;

const imageSrc = computed(() => {
  if (import.meta.server) return useImages().getImageSrc('404_background.png');

  if (isMobile.value) {
    return useImages().getImageSrc('404_background_mobile.png');
  } else {
    return useImages().getImageSrc('404_background.png');
  }
});

function onNavigateToArea(area: AreaData) {
  const route = area.title.toLowerCase().replace(' ', '-');
  navigateTo(`/${route}`);
}
</script>
